import {
  IconFavoriteBorder,
  IconFile,
  IconFundingReport,
  IconHouse,
  IconMoney,
  IconSpeed,
} from '@capitalise/ui-design-system';
import { CountryList } from 'enums/country';
import { sidebar } from 'enums/sidebar';
import { useAuthContext } from 'HOC/withAuth';
import { useFeatureFlagContext } from 'hooks/useFeatureFlags';
import { CompanyType } from '../shared/types';
import { MembershipFeatureFlagKeys } from '../shared/typesTS';
import { useMembershipFeatureFlag } from './useMembershipFeatureFlag';

const useBusinessMenu = (path: string) => {
  const { user: auth } = useAuthContext();
  const [hasTodayAccess, hasCreditProfileAccess] = useMembershipFeatureFlag(
    MembershipFeatureFlagKeys.productToday,
    MembershipFeatureFlagKeys.productCreditProfile,
  );

  const flags = useFeatureFlagContext();
  const hasCapitalReport = flags?.capitalReport;
  const hasFeatureFunding = flags?.feature_funding === true;
  const entity = auth?.company?.company_id;
  const countryCode = auth?.locale?.countryCode;
  const isGB = countryCode === CountryList.GREAT_BRITAIN;
  const companyType = auth?.company?.type;

  const isLLPorLimited = [
    CompanyType.limitedCompany,
    CompanyType.limitedLiabilityPartnership,
  ].includes(companyType);

  const shouldDisplayCreditProfile = isLLPorLimited && hasCreditProfileAccess && isGB;
  const shouldDisplayToday = hasTodayAccess;

  const linkMatchUrl = (to: string) => {
    const pathname = path;
    return pathname?.endsWith(to);
  };

  const menuItems = [
    {
      item: {
        icon: <IconHouse />,
        label: 'Today',
        mobileLabel: 'Today',
      },
      group: sidebar.today,
      url: '/business/today',
      visible: shouldDisplayToday,
      hideMobile: false,
      active: linkMatchUrl('/business/today'),
    },
    {
      item: {
        icon: <IconSpeed />,
        label: 'Your credit profile',
        mobileLabel: 'Credit profile',
      },
      group: sidebar.analysis,
      url: '/business/credit-profile',
      visible: shouldDisplayCreditProfile,
      hideMobile: false,
      active: linkMatchUrl('/business/credit-profile'),
    },
    {
      item: {
        icon: <IconFundingReport />,
        label: 'Capital report',
        mobileLabel: 'Capital',
      },
      group: sidebar.analysis,
      url: `/business/capital-report/${entity}`,
      visible: hasCapitalReport,
      hideMobile: true,
      active: linkMatchUrl(`/business/capital-report/${entity}`),
    },
    {
      item: {
        icon: <IconFavoriteBorder />,
        label: 'Credit risk manager',
        mobileLabel: 'Risk manager',
      },
      group: sidebar.analysis,
      url: '/business/monitor',
      visible: isGB,
      hideMobile: false,
      active: linkMatchUrl('/business/monitor'),
    },
    {
      item: {
        icon: <IconMoney />,
        label: 'Find funding',
        mobileLabel: 'Funding',
      },
      group: sidebar.marketplace,
      url: '/business/funding',
      visible: hasFeatureFunding,
      hideMobile: false,
      active: linkMatchUrl('/business/funding'),
    },
    {
      item: {
        icon: <IconFile />,
        label: 'Applications',
        mobileLabel: 'Applications',
      },
      group: sidebar.marketplace,
      url: `/business/applications`,
      visible: true,
      hideMobile: true,
      active: linkMatchUrl('/business/applications'),
    },
  ];

  return {
    menuItems: menuItems.filter((menu) => menu.visible),
    mobileMenuItems: menuItems.filter((menu) => !menu.hideMobile),
    activeIndex: menuItems.filter((menu) => menu.visible).findIndex((item) => item.active) + 1,
    isGB,
  };
};

export default useBusinessMenu;
